import { useEffect, useMemo, useState } from 'react';
import { apiGet } from '../../services/apiClient';

const toLocalIsoDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const todayIso = () => toLocalIsoDate(new Date());
const daysAgoIso = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toLocalIsoDate(date);
};

const formatNumber = (value) => Number(value || 0).toLocaleString('en-IN');
const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const StatCard = ({ label, value, icon }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        <p className="mt-1 text-2xl font-bold text-slate-950">{formatNumber(value)}</p>
      </div>
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-lg text-emerald-700">
        <i className={`bi ${icon}`} />
      </span>
    </div>
  </div>
);

const eventLabel = (type) =>
  String(type || 'event')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

export default function AdminVisitorAnalytics() {
  const [filters, setFilters] = useState({ from: daysAgoIso(7), to: todayIso() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', `${filters.to}T23:59:59`);
    params.set('limit', '150');
    return params.toString();
  }, [filters]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiGet(`/api/admin/visitor-analytics?${query}`);
      setData(response);
    } catch (err) {
      setError(err.message || 'Unable to load visitor analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const summary = data?.summary || {};
  const topPages = data?.topPages || [];
  const eventTypes = data?.eventTypes || [];
  const sessions = data?.sessions || [];
  const recentEvents = data?.recentEvents || [];

  return (
    <div className="space-y-4">
      <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Tracking analytics</p>
            <h1 className="admin-page-title text-xl font-bold text-slate-950">Visitor Activity</h1>
            <p className="text-sm text-slate-600">Page visits, clicks, form submits, and user journey sessions.</p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="grid gap-1 text-xs font-semibold text-slate-600">
              From
              <input
                type="date"
                value={filters.from}
                onChange={(event) => setFilters((prev) => ({ ...prev, from: event.target.value }))}
                className="h-10 rounded-xl border border-slate-200 px-3 text-sm text-slate-900"
              />
            </label>
            <label className="grid gap-1 text-xs font-semibold text-slate-600">
              To
              <input
                type="date"
                value={filters.to}
                onChange={(event) => setFilters((prev) => ({ ...prev, to: event.target.value }))}
                className="h-10 rounded-xl border border-slate-200 px-3 text-sm text-slate-900"
              />
            </label>
            <button
              type="button"
              onClick={load}
              className="h-10 rounded-xl bg-slate-950 px-4 text-sm font-semibold text-white hover:bg-slate-800"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {error ? <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
      {loading ? <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">Loading analytics...</div> : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Visitors" value={summary.visitors} icon="bi-people" />
        <StatCard label="Sessions" value={summary.sessions} icon="bi-window-stack" />
        <StatCard label="Page Views" value={summary.pageViews} icon="bi-eye" />
        <StatCard label="Total Activity" value={summary.totalEvents} icon="bi-activity" />
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.2fr,0.8fr]">
        <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-base font-bold text-slate-950">Top Pages</h2>
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pr-3">Page</th>
                  <th className="py-2 pr-3 text-right">Views</th>
                  <th className="py-2 text-right">Sessions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {topPages.map((page) => (
                  <tr key={page.path}>
                    <td className="max-w-[420px] py-3 pr-3 font-medium text-slate-950">
                      <span className="block truncate">{page.path}</span>
                    </td>
                    <td className="py-3 pr-3 text-right">{formatNumber(page.views)}</td>
                    <td className="py-3 text-right">{formatNumber(page.sessions)}</td>
                  </tr>
                ))}
                {!topPages.length ? (
                  <tr>
                    <td colSpan="3" className="py-6 text-center text-slate-500">No page views for this range.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-base font-bold text-slate-950">Activity Types</h2>
          <div className="mt-4 space-y-3">
            {eventTypes.map((item) => (
              <div key={item.eventType} className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="font-semibold text-slate-800">{eventLabel(item.eventType)}</span>
                  <span className="font-bold text-slate-950">{formatNumber(item.count)}</span>
                </div>
              </div>
            ))}
            {!eventTypes.length ? <p className="py-6 text-center text-sm text-slate-500">No activity yet.</p> : null}
          </div>
        </section>
      </div>

      <div className="grid gap-4 xl:grid-cols-[0.9fr,1.1fr]">
        <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-base font-bold text-slate-950">Recent Sessions</h2>
          <div className="mt-3 space-y-2">
            {sessions.map((session) => (
              <div key={session.sessionId} className="rounded-2xl border border-slate-100 bg-white px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900">{session.sessionId}</p>
                    <p className="text-xs text-slate-500">{formatDateTime(session.lastSeen)}</p>
                  </div>
                  <div className="text-right text-xs text-slate-500">
                    <p><strong className="text-slate-900">{formatNumber(session.pages)}</strong> pages</p>
                    <p><strong className="text-slate-900">{formatNumber(session.events)}</strong> events</p>
                  </div>
                </div>
              </div>
            ))}
            {!sessions.length ? <p className="py-6 text-center text-sm text-slate-500">No sessions for this range.</p> : null}
          </div>
        </section>

        <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="text-base font-bold text-slate-950">Recent Activity</h2>
          <div className="mt-3 max-h-[620px] overflow-auto divide-y divide-slate-100">
            {recentEvents.map((event) => (
              <article key={event.id} className="py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
                    {eventLabel(event.event_type)}
                  </span>
                  <span className="text-xs text-slate-500">{formatDateTime(event.created_at)}</span>
                </div>
                <p className="mt-2 truncate text-sm font-semibold text-slate-950">{event.path}</p>
                {event.element_label ? <p className="mt-1 text-sm text-slate-600">Action: {event.element_label}</p> : null}
                <p className="mt-1 truncate text-xs text-slate-400">Session: {event.session_id}</p>
              </article>
            ))}
            {!recentEvents.length ? <p className="py-6 text-center text-sm text-slate-500">No activity for this range.</p> : null}
          </div>
        </section>
      </div>
    </div>
  );
}
